import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";

import logo1 from "../assets/deme1.png";
import logo2 from "../assets/deme2.png";
import logo3 from "../assets/deme3.png";

import "./navbar.css";

/* =========================================================
   NAVIGATION DATA
========================================================= */

const NAV_LINKS = [
  {
    name: "Search Schemes",
    path: "/SchemeSearch",
    icon: "fa-book",
  },
  {
    name: "Prayas",
    path: "/Prayas",
    icon: "fa-graduation-cap",
  },
  {
    name: "Student Question Bank",
    path: "/Feedback",
    icon: "fa-file-text-o",
  },
  {
    name: "Register Now",
    path: "/register",
    icon: "fa-user-plus",
  },
];

const LOGIN_LINK = {
  name: "Login Panel ",
  path: "/login-panels",
  icon: "fa-sign-in",
};

const REPORT_LINKS = [
  {
    name: "Student Report",
    path: "/Student-Report-Dashbord",
    icon: "fa-user",
  },
  {
    name: "Staff Report",
    path: "/Staff-Report",
    icon: "fa-calendar-check-o",
  },
  {
    name: "School Report",
    path: "/School-Report",
    icon: "fa-building-o",
  },
];

/* =========================================================
   FOCUS TRAP
========================================================= */

function useFocusTrap(containerRef, isActive) {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;

    const selector =
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const getFocusable = () =>
      Array.from(container.querySelectorAll(selector));

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;

      const focusable = getFocusable();

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }

      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const focusable = getFocusable();

    requestAnimationFrame(() => {
      focusable[0]?.focus();
    });

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isActive, containerRef]);
}

/* =========================================================
   CLICK OUTSIDE
========================================================= */

function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }

      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

/* =========================================================
   NAVBAR
========================================================= */

function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopReportsOpen, setIsDesktopReportsOpen] = useState(false);
  const [isMobileReportsOpen, setIsMobileReportsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);

  const location = useLocation();

  const drawerRef = useRef(null);
  const reportsRef = useRef(null);

  /* CLOSE MOBILE MENU */

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsMobileReportsOpen(false);
  }, []);

  /* CLOSE EVERYTHING */

  const closeAll = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsMobileReportsOpen(false);
    setIsDesktopReportsOpen(false);
  }, []);

  /* MOBILE MENU TOGGLE */

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => {
      const next = !prev;

      if (next) {
        setIsMobileReportsOpen(false);
      }

      return next;
    });

    setIsDesktopReportsOpen(false);
  }, []);

  /* MOBILE REPORT TOGGLE */

  const toggleMobileReports = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsMobileReportsOpen((prev) => !prev);
  }, []);

  /* DESKTOP REPORT TOGGLE */

  const toggleDesktopReports = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDesktopReportsOpen((prev) => !prev);
    setIsMobileReportsOpen(false);
  }, []);

  /* MOBILE REPORT CLICK */

  const handleMobileReportClick = useCallback(() => {
    setIsMobileReportsOpen(false);
    setIsMobileMenuOpen(false);
    setIsDesktopReportsOpen(false);
  }, []);

  /* MOUNT */

  useEffect(() => {
    setMounted(true);
  }, []);

  /* BODY LOCK */

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileMenuOpen]);

  /* ESCAPE KEY */

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeAll();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeAll]);

  /* ROUTE CHANGE */

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileReportsOpen(false);
    setIsDesktopReportsOpen(false);
  }, [location.pathname]);

  /* SCROLL */

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* CLICK OUTSIDE DESKTOP REPORTS */

  useClickOutside(reportsRef, () => {
    setIsDesktopReportsOpen(false);
  });

  /* FOCUS TRAP */

  useFocusTrap(drawerRef, isMobileMenuOpen);

  /* ACTIVE ROUTE */

  const isActive = useCallback(
    (path) => {
      if (path === "/") {
        return location.pathname === "/";
      }

      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      {/* =====================================================
          PREMIUM TOP HEADER
      ===================================================== */}

      <header className={`top-header ${scrolled ? "scrolled" : ""}`}>
        <div className="header-glow" />

        <div className="header-container">

          {/* LEFT LOGO + DECORATION */}

          <div className="logo-left logo-card">
            <Link
              to="/Student-school-darpan"
              onClick={closeMobileMenu}
              aria-label="School Darpan"
            >
              <span className="logo-shine" />
              <img src={logo2} alt="School Darpan" loading="eager" />
            </Link>

            <div className="header-deco deco-left" aria-hidden="true">
              <span className="deco-line" />
              <span className="deco-chip">
                <i className="fa fa-graduation-cap" /> Digital Education
              </span>
            </div>
          </div>

          {/* CENTER LOGO */}

          <div className="logo-center">
            <Link
              to="/Student-hub"
              onClick={closeMobileMenu}
              aria-label="Citizen Window"
            >
              <span className="center-logo-glow" />
              <img src={logo3} alt="Citizen Window" loading="eager" />
            </Link>
          </div>

          {/* RIGHT LOGO + DECORATION */}

          <div className="logo-right logo-card">
            <div className="header-deco deco-right" aria-hidden="true">
              <span className="deco-chip">
                <i className="fa fa-users" /> Student HUB  
              </span>
              <span className="deco-line" />
            </div>

            <Link to="/" onClick={closeMobileMenu} aria-label="Student Window">
              <span className="logo-shine" />
              <img src={logo1} alt="Student Window" loading="eager" />
            </Link>
          </div>

          {/* MOBILE BUTTON */}

          <button
            type="button"
            className={`mobile-menu-btn ${isMobileMenuOpen ? "open" : ""}`}
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      {/* =====================================================
          DESKTOP NAVBAR
      ===================================================== */}

      <nav className="main-navbar" aria-label="Primary navigation">
        <div className="nav-glow-line" />

        <div className="nav-container">

          {/* CENTER GROUP: HOME + LINKS */}
          <div className="nav-center-group">

            {/* HOME */}

            <Link
              to="/"
              className={`desktop-home ${
                location.pathname === "/" ? "active" : ""
              }`}
              aria-label="Home"
            >
              <span className="home-icon-wrap">
                <i className="fa fa-home" aria-hidden="true" />
              </span>
              <span className="home-tooltip">Home</span>
            </Link>

            {/* LINKS */}

            <ul className="nav-links">

              {/* REPORTS */}

              <li
                ref={reportsRef}
                className={`reports-menu ${isDesktopReportsOpen ? "open" : ""}`}
              >
                <button
                  type="button"
                  className="reports-trigger"
                  onClick={toggleDesktopReports}
                  aria-expanded={isDesktopReportsOpen}
                >
                  <span className="nav-icon-box">
                    <i className="fa fa-bar-chart" aria-hidden="true" />
                  </span>

                  <span className="nav-text">Reports</span>

                  <i
                    className={`fa ${
                      isDesktopReportsOpen ? "fa-angle-up" : "fa-angle-down"
                    } reports-arrow`}
                    aria-hidden="true"
                  />
                </button>

                <ul className="reports-dropdown" role="menu">
                  {REPORT_LINKS.map((report, index) => (
                    <li
                      key={report.name}
                      style={{ "--dropdown-index": index }}
                    >
                      <Link
                        to={report.path}
                        role="menuitem"
                        onClick={() => setIsDesktopReportsOpen(false)}
                      >
                        <span className="report-icon">
                          <i className={`fa ${report.icon}`} aria-hidden="true" />
                        </span>

                        <span className="report-content">
                          <strong>{report.name}</strong>
                          <small>View detailed report</small>
                        </span>

                        <span className="report-arrow-box">
                          <i className="fa fa-angle-right" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>

              {/* NORMAL NAV LINKS */}

              {NAV_LINKS.map((link, index) => (
                <li
                  key={`${link.name}-${index}`}
                  className={isActive(link.path) ? "active" : ""}
                  style={{ "--item-index": index }}
                >
                  <Link
                    to={link.path}
                    onClick={() => setIsDesktopReportsOpen(false)}
                  >
                    <span className="nav-icon-box">
                      <i className={`fa ${link.icon}`} aria-hidden="true" />
                    </span>

                    <span className="nav-text">{link.name}</span>
                    <span className="nav-hover-dot" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* RIGHT SIDE: LOGIN BUTTON */}

          <Link
            to={LOGIN_LINK.path}
            className="desktop-login-btn"
            onClick={() => setIsDesktopReportsOpen(false)}
          >
            <span className="login-icon-box">
              <i className={`fa ${LOGIN_LINK.icon}`} aria-hidden="true" />
            </span>
            <span className="login-text">{LOGIN_LINK.name}</span>
          </Link>
        </div>
      </nav>

      {/* =====================================================
          MOBILE DRAWER
      ===================================================== */}

      {mounted &&
        createPortal(
          <>
            {/* OVERLAY */}

            <div
              className={`mobile-menu-overlay ${
                isMobileMenuOpen ? "active" : ""
              }`}
              onClick={closeMobileMenu}
              aria-hidden="true"
            />

            {/* DRAWER */}

            <aside
              ref={drawerRef}
              className={`mobile-menu-drawer ${
                isMobileMenuOpen ? "active" : ""
              }`}
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation"
            >
              <div className="drawer-orb orb-one" />
              <div className="drawer-orb orb-two" />

              {/* MOBILE HEADER */}

              <div className="mobile-menu-header">
                <div className="mobile-brand">
                  <span className="mobile-brand-icon">
                    <i className="fa fa-graduation-cap" />
                  </span>

                  <div>
                    <small>STUDENT SCHOOL</small>
                    <strong>DARPAN</strong>
                  </div>
                </div>

                <button
                  type="button"
                  className="close-btn"
                  onClick={closeMobileMenu}
                  aria-label="Close menu"
                >
                  <i className="fa fa-times" aria-hidden="true" />
                </button>
              </div>

              {/* PORTAL HOME */}

              <Link to="/" className="portal-home-btn" onClick={closeMobileMenu}>
                <span className="portal-home-icon">
                  <i className="fa fa-home" aria-hidden="true" />
                </span>

                <span>
                  <small>WELCOME TO</small>
                  <strong>Portal Home</strong>
                </span>

                <i className="fa fa-arrow-right portal-arrow" aria-hidden="true" />
              </Link>

              {/* REPORTS SECTION */}

              <div className="mobile-section-title report-title">
                <span />
                REPORTS
                <span />
              </div>

              <ul className="mobile-nav-list mobile-reports-top">
                <li className="mobile-report-wrapper">
                  <button
                    type="button"
                    className={`mobile-reports-toggle ${
                      isMobileReportsOpen ? "open" : ""
                    }`}
                    onClick={toggleMobileReports}
                    aria-expanded={isMobileReportsOpen}
                    aria-controls="mobile-report-submenu"
                  >
                    <span className="mobile-icon">
                      <i className="fa fa-bar-chart" aria-hidden="true" />
                    </span>

                    <span className="mobile-link-text">Reports Center</span>

                    <span className="mobile-arrow">
                      <i
                        className={`fa ${
                          isMobileReportsOpen
                            ? "fa-angle-down"
                            : "fa-angle-right"
                        }`}
                        aria-hidden="true"
                      />
                    </span>
                  </button>

                  <ul
                    id="mobile-report-submenu"
                    className={`mobile-reports-submenu ${
                      isMobileReportsOpen ? "open" : ""
                    }`}
                    aria-hidden={!isMobileReportsOpen}
                  >
                    {REPORT_LINKS.map((report) => (
                      <li key={report.name}>
                        <Link
                          to={report.path}
                          onClick={handleMobileReportClick}
                        >
                          <span className="submenu-icon">
                            <i
                              className={`fa ${report.icon}`}
                              aria-hidden="true"
                            />
                          </span>

                          <span>{report.name}</span>

                          <i className="fa fa-angle-right" aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>

              {/* MAIN MENU */}

              <div className="mobile-section-title">
                <span />
                MAIN MENU
                <span />
              </div>

              <ul className="mobile-nav-list">
                {NAV_LINKS.map((link, index) => (
                  <li
                    key={`${link.name}-${index}`}
                    className={isActive(link.path) ? "active" : ""}
                    style={{ "--mobile-index": index }}
                  >
                    <Link to={link.path} onClick={closeMobileMenu}>
                      <span className="mobile-icon">
                        <i className={`fa ${link.icon}`} aria-hidden="true" />
                      </span>

                      <span className="mobile-link-text">{link.name}</span>

                      <span className="mobile-arrow">
                        <i className="fa fa-angle-right" />
                      </span>
                    </Link>
                  </li>
                ))}

                {/* MOBILE LOGIN BUTTON */}

                <li className="mobile-login-wrapper">
                  <Link
                    to={LOGIN_LINK.path}
                    className="mobile-login-btn"
                    onClick={closeMobileMenu}
                  >
                    <span className="mobile-icon">
                      <i className={`fa ${LOGIN_LINK.icon}`} aria-hidden="true" />
                    </span>

                    <span className="mobile-link-text">{LOGIN_LINK.name}</span>

                    <span className="mobile-arrow">
                      <i className="fa fa-angle-right" />
                    </span>
                  </Link>
                </li>
              </ul>

              {/* DRAWER FOOTER */}

              <div className="mobile-menu-footer">
                <div className="footer-line">
                  <span />
                  <i className="fa fa-graduation-cap" />
                  <span />
                </div>

                <strong>Integrated Student School Darpan</strong>
                <small>Digital Education • Citizen Services</small>
              </div>
            </aside>
          </>,
          document.body
        )}

      {/* LOADER */}

      <div
        id="divLoaderImage"
        className="LoaderImage"
        style={{ display: "none" }}
      />
    </>
  );
}

export default Navbar;